import type { GitHubConfig } from '../types';
import {
  encodeBytesAsBase64Content,
  GITHUB_DIRECT_BLOB_MAX_BYTES,
  isLikelyCorsOrNetworkError,
  pointerFileContent,
  resolveCommittedMediaBytes,
  tryParseStoredLfsPointer,
  uploadGitLfsObject,
} from './githubLfs';
import { sniffImageMime } from '../utils/imageDisplay';
import { pathUsesGitLfs, shouldWriteMediaWithGitLfs } from '../utils/gitLfsPointer';
import { githubMediaBlobUrl, isGithubCdnMediaUrl } from '../utils/githubMediaUrl';
import { withGitBusy } from '../utils/gitBusy';

interface ContentsResponse {
  content?: string;
  encoding?: string;
  sha: string;
  name?: string;
  path?: string;
  download_url?: string;
  size?: number;
}

interface TreeItem {
  path: string;
  type: string;
}

interface FileResponse {
  content: string;
  sha: string;
  name: string;
  path: string;
}

export class GitHubService {
  private readonly base: string;
  private readonly headers: HeadersInit;
  /** Avoid stale tree/blob metadata (especially SHA) from HTTP caches on mobile. */
  private readonly fetchInit: RequestInit = { cache: 'no-store' };

  constructor(private readonly config: GitHubConfig) {
    this.base = `https://api.github.com/repos/${config.owner}/${config.repo}`;
    this.headers = {
      Authorization: `Bearer ${config.pat}`,
      Accept: 'application/vnd.github+json',
      'X-GitHub-Api-Version': '2022-11-28',
    };
  }

  getConfig(): Readonly<GitHubConfig> {
    return this.config;
  }

  private githubFetch(url: string, init: RequestInit = {}): Promise<Response> {
    const headers = { ...(this.headers as Record<string, string>), ...(init.headers as Record<string, string> | undefined) };
    return fetch(url, { ...this.fetchInit, ...init, headers });
  }

  /** CDN / signed download URLs — no GitHub API headers (avoids CORS NetworkError in browser). */
  private binaryFetch(url: string): Promise<Response> {
    return fetch(url, this.fetchInit);
  }

  private contentsUrl(path: string): string {
    return `${this.base}/contents/${path}?ref=${encodeURIComponent(this.config.branch)}`;
  }

  private refHeadsUrl(branch: string): string {
    return `${this.base}/git/ref/heads/${encodeURIComponent(branch)}`;
  }

  /**
   * Create `config.branch` from the repository default branch when it does not exist yet.
   * No-op if the branch is already present.
   */
  async ensureBranchExists(): Promise<void> {
    return withGitBusy(() => this.ensureBranchExistsCore());
  }

  private async ensureBranchExistsCore(): Promise<void> {
    const branch = this.config.branch.trim() || 'main';
    const existing = await this.githubFetch(this.refHeadsUrl(branch));
    if (existing.ok) return;
    if (existing.status !== 404) {
      throw new Error(`GitHub branch check error ${existing.status}: ${await existing.text()}`);
    }

    const repoRes = await this.githubFetch(this.base);
    if (!repoRes.ok) {
      throw new Error(`GitHub repo error ${repoRes.status}: ${await repoRes.text()}`);
    }
    const repo = (await repoRes.json()) as { default_branch?: string };
    const baseBranch = (repo.default_branch?.trim() || 'main');

    if (baseBranch === branch) {
      throw new Error(
        `Branch "${branch}" was not found and matches the repository default. The repository may be empty or the token may lack access.`
      );
    }

    const baseRefRes = await this.githubFetch(this.refHeadsUrl(baseBranch));
    if (!baseRefRes.ok) {
      throw new Error(`GitHub base branch error ${baseRefRes.status}: ${await baseRefRes.text()}`);
    }
    const baseRef = (await baseRefRes.json()) as { object: { sha: string } };
    const sha = baseRef.object?.sha;
    if (!sha) throw new Error(`Could not read commit SHA for branch "${baseBranch}".`);

    const createRes = await this.githubFetch(`${this.base}/git/refs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ref: `refs/heads/${branch}`, sha }),
    });
    if (createRes.ok) return;

    const errText = await createRes.text();
    if (createRes.status === 422 && /Reference already exists/i.test(errText)) return;

    throw new Error(`GitHub create branch error ${createRes.status}: ${errText}`);
  }

  private static base64ToBytes(b64: string): Uint8Array {
    const bin = atob(b64.replace(/\s/g, ''));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)!;
    return bytes;
  }

  private async readBytesFromResponse(res: Response): Promise<Uint8Array | null> {
    if (!res.ok) return null;
    const buf = await res.arrayBuffer();
    if (buf.byteLength === 0) return null;
    return new Uint8Array(buf);
  }

  async listMarkdownFiles(prefix: string): Promise<string[]> {
    const blobs = await this.listBlobPathsUnderPrefix(prefix);
    return blobs.filter(p => p.endsWith('.md')).sort();
  }

  /** All blob paths under `prefix` (prefix may be `database/foo` or `database/foo/`). */
  async listBlobPathsUnderPrefix(prefix: string): Promise<string[]> {
    return withGitBusy(() => this.listBlobPathsUnderPrefixCore(prefix));
  }

  private async listBlobPathsUnderPrefixCore(prefix: string): Promise<string[]> {
    const normalized = prefix.endsWith('/') ? prefix : `${prefix}/`;
    const res = await this.githubFetch(
      `${this.base}/git/trees/${this.config.branch}?recursive=1`
    );
    if (!res.ok) throw new Error(`GitHub API error ${res.status}: ${await res.text()}`);
    const data = await res.json() as { tree: TreeItem[] };
    return data.tree
      .filter(item => item.type === 'blob' && item.path.startsWith(normalized))
      .map(item => item.path);
  }

  async getBlobSha(path: string): Promise<string | null> {
    return withGitBusy(() => this.getBlobShaCore(path));
  }

  private async getBlobShaCore(path: string): Promise<string | null> {
    const res = await this.githubFetch(this.contentsUrl(path));
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`GitHub API error ${res.status}: ${await res.text()}`);
    const data = await res.json() as { sha: string };
    return data.sha;
  }

  /**
   * Load file bytes from GitHub. Photos over ~1 MB have no base64 `content` in the JSON
   * response — use raw media type, download_url, or git blobs API.
   */
  private isImagePayload(res: Response, bytes: Uint8Array, pathHint = ''): boolean {
    const ct = (res.headers.get('content-type') ?? '').toLowerCase();
    if (ct.startsWith('image/')) return true;
    if (sniffImageMime(bytes) != null) return true;
    if (bytes.length > 0 && /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(pathHint)) return true;
    return false;
  }

  private isBinaryPayload(res: Response, bytes: Uint8Array, pathHint = ''): boolean {
    if (tryParseStoredLfsPointer(bytes)) return false;
    if (bytes.length >= 4) {
      const head = new TextDecoder().decode(bytes.slice(0, 64)).trimStart();
      if (head.startsWith('<') || head.startsWith('{') || head.startsWith('[')) return false;
    }
    if (pathUsesGitLfs(pathHint)) {
      const ct = (res.headers.get('content-type') ?? '').toLowerCase();
      if (ct.startsWith('image/') || ct.startsWith('audio/') || ct.startsWith('video/')) return true;
      if (sniffImageMime(bytes) != null) return true;
      if (bytes.length > 0) return true;
    }
    return this.isImagePayload(res, bytes, pathHint);
  }

  private async fetchRawBytes(url: string): Promise<Uint8Array | null> {
    const rawAccepts = [
      'application/vnd.github.raw',
      'application/vnd.github.v3.raw',
      'application/vnd.github.raw+json',
    ];
    for (const accept of rawAccepts) {
      const rawRes = await this.githubFetch(url, {
        headers: { ...(this.headers as Record<string, string>), Accept: accept },
      });
      const bytes = await this.readBytesFromResponse(rawRes);
      if (bytes && this.isBinaryPayload(rawRes, bytes, url)) return bytes;
    }
    return null;
  }

  private async finalizeReadBytes(bytes: Uint8Array): Promise<Uint8Array> {
    return resolveCommittedMediaBytes(this.config, bytes);
  }

  /**
   * GitHub `download_url` for media is usually a CDN URL (often with ?token=). Never send API
   * Authorization headers there — browsers CORS-fail. Fall back to Contents/raw API instead.
   */
  private async fetchDownloadUrl(url: string, pathHint = ''): Promise<Uint8Array | null> {
    const hint = pathHint || url;
    const fetchers: Array<(u: string) => Promise<Response>> = isGithubCdnMediaUrl(url)
      ? [this.binaryFetch.bind(this)]
      : [this.binaryFetch.bind(this), this.githubFetch.bind(this)];
    for (const fetcher of fetchers) {
      const dlRes = await fetcher(url);
      if (!dlRes.ok) continue;
      const bytes = await this.readBytesFromResponse(dlRes);
      if (!bytes || !this.isBinaryPayload(dlRes, bytes, hint)) continue;
      try {
        return await this.finalizeReadBytes(bytes);
      } catch {
        if (tryParseStoredLfsPointer(bytes)) continue;
        return null;
      }
    }
    return null;
  }

  /**
   * HTTPS URL for streaming playback (Range requests). Prefer on mobile over blob URLs.
   * Returns download_url from the Contents API, or media.githubusercontent.com for media paths.
   */
  async getMediaStreamUrl(path: string): Promise<string | null> {
    return withGitBusy(() => this.getMediaStreamUrlCore(path));
  }

  private async getMediaStreamUrlCore(path: string): Promise<string | null> {
    const metaRes = await this.githubFetch(this.contentsUrl(path));
    if (!metaRes.ok) return null;
    const meta = (await metaRes.json()) as ContentsResponse;
    if (meta.download_url) return meta.download_url;
    if (pathUsesGitLfs(path)) return githubMediaBlobUrl(this.config, path);
    return null;
  }

  async readBinary(path: string): Promise<{ bytes: Uint8Array; sha: string }> {
    return withGitBusy(() => this.readBinaryCore(path));
  }

  private async readBinaryCore(path: string): Promise<{ bytes: Uint8Array; sha: string }> {
    const url = this.contentsUrl(path);
    const metaRes = await this.githubFetch(url);
    if (!metaRes.ok) {
      throw new Error(`GitHub readBinary ${path}: HTTP ${metaRes.status}`);
    }
    const meta = await metaRes.json() as ContentsResponse;
    const sha = meta.sha;
    const size = meta.size ?? 0;
    const large = size > 900_000;
    const normalized = meta.content?.replace(/\s/g, '') ?? '';
    const isLfsMedia = pathUsesGitLfs(path);

    // Real bytes live on GitHub media / download_url — never use raw Contents API for LFS paths
    // (raw+json returns the 132-byte pointer text, which cannot play in video/audio/img).
    if (meta.download_url) {
      const dl = await this.fetchDownloadUrl(meta.download_url, path);
      if (dl) return { bytes: dl, sha };
    }
    if (isLfsMedia) {
      const dl = await this.fetchDownloadUrl(githubMediaBlobUrl(this.config, path), path);
      if (dl) return { bytes: dl, sha };
    }

    // Small files (thumbnails): inline base64 in the JSON response.
    if (normalized && !large) {
      try {
        const bytes = GitHubService.base64ToBytes(normalized);
        if (bytes.length > 0) {
          const media = await this.finalizeReadBytes(bytes);
          return { bytes: media, sha };
        }
      } catch {
        /* fall through */
      }
    }

    // Large originals: never raw-fetch LFS paths (pointer only). Use git blobs API as fallback.
    if (large) {
      if (!isLfsMedia) {
        const rawBytes = await this.fetchRawBytes(url);
        if (rawBytes) return { bytes: await this.finalizeReadBytes(rawBytes), sha };
      }
      throw new Error(`GitHub readBinary: could not load large file ${path} (${size} bytes)`);
    }

    if (!isLfsMedia) {
      const rawBytes = await this.fetchRawBytes(url);
      if (rawBytes) return { bytes: await this.finalizeReadBytes(rawBytes), sha };
    }

    const blobRes = await this.githubFetch(`${this.base}/git/blobs/${sha}`);
    if (blobRes.ok) {
      const blob = await blobRes.json() as { content?: string; encoding?: string };
      if (blob.content && blob.encoding === 'base64') {
        try {
          const bytes = GitHubService.base64ToBytes(blob.content);
          if (
            bytes.length > 0 &&
            (sniffImageMime(bytes) != null ||
              /\.(jpe?g|png|gif|webp|heic|heif|mp4|m4v|webm|mov|mkv|mp3|m4a|wav|ogg)$/i.test(path) ||
              pathUsesGitLfs(path))
          ) {
            const media = await this.finalizeReadBytes(bytes);
            return { bytes: media, sha };
          }
        } catch {
          /* fall through */
        }
      }
    }

    throw new Error(`GitHub readBinary: could not load bytes for ${path} (${size} bytes)`);
  }

  async writeBinary(
    path: string,
    data: ArrayBuffer | Uint8Array,
    sha: string,
    message: string
  ): Promise<string | undefined> {
    return withGitBusy(() => this.writeBinaryCore(path, data, sha, message));
  }

  private async encodeContentsPayload(path: string, bytes: Uint8Array): Promise<string> {
    if (!shouldWriteMediaWithGitLfs(path, bytes.byteLength)) {
      return encodeBytesAsBase64Content(bytes);
    }
    try {
      return btoa(pointerFileContent(await uploadGitLfsObject(this.config, bytes)));
    } catch (err) {
      if (bytes.byteLength <= GITHUB_DIRECT_BLOB_MAX_BYTES && isLikelyCorsOrNetworkError(err)) {
        return encodeBytesAsBase64Content(bytes);
      }
      throw err;
    }
  }

  private async writeBinaryCore(
    path: string,
    data: ArrayBuffer | Uint8Array,
    sha: string,
    message: string
  ): Promise<string | undefined> {
    const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
    const encoded = await this.encodeContentsPayload(path, bytes);
    const body: Record<string, unknown> = { message, content: encoded, branch: this.config.branch };
    if (sha) body['sha'] = sha;
    const res = await this.githubFetch(`${this.base}/contents/${path}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`GitHub write error ${res.status}: ${text}`);
    try {
      const parsed = JSON.parse(text) as { content?: { sha?: string } };
      return typeof parsed.content?.sha === 'string' ? parsed.content.sha : undefined;
    } catch {
      return undefined;
    }
  }

  async readFile(path: string): Promise<{ content: string; sha: string }> {
    return withGitBusy(() => this.readFileCore(path));
  }

  private async readFileCore(path: string): Promise<{ content: string; sha: string }> {
    const res = await this.githubFetch(this.contentsUrl(path));
    if (!res.ok) throw new Error(`GitHub API error ${res.status}: ${await res.text()}`);
    const data = await res.json() as FileResponse;
    const content = decodeURIComponent(escape(atob(data.content.replace(/\s/g, ''))));
    return { content, sha: data.sha };
  }

  async writeFile(
    path: string,
    content: string,
    sha: string,
    message: string
  ): Promise<string | undefined> {
    return withGitBusy(() => this.writeFileCore(path, content, sha, message));
  }

  private async writeFileCore(
    path: string,
    content: string,
    sha: string,
    message: string
  ): Promise<string | undefined> {
    const encoded = btoa(unescape(encodeURIComponent(content)));
    const body: Record<string, unknown> = { message, content: encoded, branch: this.config.branch };
    if (sha) body['sha'] = sha;
    const res = await this.githubFetch(`${this.base}/contents/${path}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    if (!res.ok) throw new Error(`GitHub write error ${res.status}: ${text}`);
    try {
      const parsed = JSON.parse(text) as { content?: { sha?: string } };
      return typeof parsed.content?.sha === 'string' ? parsed.content.sha : undefined;
    } catch {
      return undefined;
    }
  }

  async deleteFile(path: string, sha: string, message: string): Promise<void> {
    return withGitBusy(() => this.deleteFileCore(path, sha, message));
  }

  private async deleteFileCore(path: string, sha: string, message: string): Promise<void> {
    const res = await this.githubFetch(`${this.base}/contents/${path}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, sha, branch: this.config.branch }),
    });
    if (!res.ok) throw new Error(`GitHub delete error ${res.status}: ${await res.text()}`);
  }

  async renameFile(oldPath: string, newPath: string, content: string, sha: string): Promise<void> {
    return withGitBusy(async () => {
      const createRes = await this.githubFetch(`${this.base}/contents/${newPath}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: `Rename ${oldPath.split('/').pop()} → ${newPath.split('/').pop()}`,
          content: btoa(unescape(encodeURIComponent(content))),
          branch: this.config.branch,
        }),
      });
      if (!createRes.ok) throw new Error(`GitHub create error ${createRes.status}: ${await createRes.text()}`);
      await this.deleteFileCore(oldPath, sha, `Remove old file after rename`);
      const { migratePageAssets } = await import('./PageAssetMigration');
      try {
        await migratePageAssets(this, oldPath, newPath);
      } catch (err) {
        console.warn('Image folder migration after rename failed:', err);
      }
    });
  }
}
